import { PlayerError } from '@foundation-player/error-codes';
import { getLogger } from '@foundation-player/logger';
import { ManifestType, StreamType } from '@foundation-player/runtime';
import type { Manifest, MediaConfig } from '@foundation-player/types';
import { Capabilities, ClientSupport } from '../capability/client-capabilities';
import { PlayerEvent } from '../event-emitter/events';
import { FoundationErrorEvent } from '../event-emitter/unfiltered';

/**
 * Determine the correct manifest according to provided capabilities.
 *
 * @remarks
 * The resolver will receive a list of manifests as well as the capabilities of the client and the player.
 * With the provided data the resolver will first do the business evaluation between HD and SD content,
 * followed by the technical evaluation according to the supported capabilities by both client and player.
 */
export class ManifestResolver {
  /**
   * Order of stream type evaluation
   *
   * @private
   */
  private readonly streamTypePriorities = [
    StreamType.HLS, // always prefer HSL because it is the most specific type
    StreamType.DASH, // use DASH where possible when HSL is not supported
    StreamType.Progressive, // only fallback to progressive when DASH and HLS are not supported
  ] as const;

  private readonly streamTypeManifestTypeMap = {
    [StreamType.HLS]: { primary: ManifestType.HlsHD, fallback: ManifestType.HlsSD },
    [StreamType.DASH]: { primary: ManifestType.DashHD, fallback: ManifestType.DashSD },
    [StreamType.Progressive]: { primary: ManifestType.Progressive, fallback: undefined },
  } as const;

  private readonly logger = getLogger('player.core.manifest.ManifestResolver');

  /**
   * Determine the manifest to use according to the provided capabilities.
   *
   * @remarks
   * The method will return the manifest of all the possibilities which should be used for this current request.
   *
   * @param mediaConfig - The media config to analyze
   * @param playerCapabilities - The capabilities returned by the root player
   * @param clientSupport - The support matrix for the current client
   */
  resolve(
    mediaConfig: Pick<MediaConfig, 'manifests'>,
    playerCapabilities: Capabilities,
    clientSupport: Pick<ClientSupport, 'capabilities'>,
  ): Manifest[] {
    const applicableCapabilities: Capabilities = {
      encryptionTypes: playerCapabilities.encryptionTypes.filter((encryptionType) =>
        clientSupport.capabilities.encryptionTypes.includes(encryptionType),
      ),
      streamTypes: playerCapabilities.streamTypes.filter((streamType) =>
        clientSupport.capabilities.streamTypes.includes(streamType),
      ),
    };
    this.logger.debug('Applicable capabilities found', {
      applicableCapabilities,
      playerCapabilities,
      clientCapabilities: clientSupport.capabilities,
    });

    const manifests: Manifest[] = [];
    const insufficientDRMCapabilities: Manifest[] = [];

    for (const streamType of this.streamTypePriorities) {
      this.logger.trace(`Try stream type priority for "${streamType}"`);
      if (!applicableCapabilities.streamTypes.includes(streamType)) {
        this.logger.trace(`Skip unsupported stream type "${streamType}"`);
        continue;
      }

      const primaryManifest = mediaConfig.manifests.find(
        (m) => m.type === this.streamTypeManifestTypeMap[streamType].primary,
      );
      if (primaryManifest) {
        this.logger.trace(`Filter primary manifest for "${streamType}"`);
        const filteredManifest = this.filterManifest(
          primaryManifest,
          applicableCapabilities,
          playerCapabilities,
          clientSupport,
        );
        if (filteredManifest) {
          manifests.push(filteredManifest);
        } else {
          insufficientDRMCapabilities.push(primaryManifest);
        }
      }

      const fallbackManifest = mediaConfig.manifests.find(
        (m) => m.type === this.streamTypeManifestTypeMap[streamType].fallback,
      );
      if (fallbackManifest) {
        this.logger.trace(`Filter fallback manifest for "${streamType}"`);
        const filteredManifest = this.filterManifest(
          fallbackManifest,
          applicableCapabilities,
          playerCapabilities,
          clientSupport,
        );
        if (filteredManifest) {
          manifests.push(filteredManifest);
        } else {
          insufficientDRMCapabilities.push(fallbackManifest);
        }
      }
    }

    if (manifests.length === 0) {
      if (insufficientDRMCapabilities.length > 0) {
        throw new FoundationErrorEvent(PlayerEvent.Error, {
          message: 'Requested DRM key systems not available!',
          code: PlayerError.NO_DRM_OPTION_AVAILABLE_ANY_MORE_32110.code,
          context: {
            manifests: insufficientDRMCapabilities,
            playerCapabilities,
            clientCapabilities: clientSupport.capabilities,
          },
        });
      } else {
        throw new FoundationErrorEvent(PlayerEvent.Error, {
          message: 'Could not determine a suitable manifest for this client and player combination!',
          code: PlayerError.NO_MANIFESTS_PLAYABLE_32210.code,
          context: {
            manifests: mediaConfig.manifests,
            playerCapabilities,
            clientCapabilities: clientSupport.capabilities,
          },
        });
      }
    }

    return manifests;
  }

  /**
   * Filter a manifest according to the provided capabilities to only include supported license servers.
   */
  private filterManifest(
    manifest: Manifest,
    applicableCapabilities: Capabilities,
    playerCapabilities: Capabilities,
    clientSupport: Pick<ClientSupport, 'capabilities'>,
  ): Manifest | undefined {
    const filteredManifest = {
      ...manifest,
      licenseServers: (manifest.licenseServers || []).filter((licenseServer) =>
        applicableCapabilities.encryptionTypes.includes(licenseServer.encryptionType),
      ),
    };

    // check if manifest requires DRM but no supported system is available
    if (manifest.licenseServers && manifest.licenseServers.length > 0 && filteredManifest.licenseServers.length === 0) {
      this.logger.debug('Requested DRM key systems not available!', {
        manifest,
        licenseServers: manifest.licenseServers,
        playerCapabilities,
        clientCapabilities: clientSupport.capabilities,
      });
      return undefined;
    }

    return filteredManifest;
  }
}
